<template>
  <div class="auth-card-wrapper">
    <div class="auth-card">
      <div class="auth-card-container">
        <img
          class="logo auth-logo"
          alt="Continuum Design"
          src="/continuum-design-logo.svg"
        />
        <h3 class="text-title">Sign up with your email address.</h3>
      </div>

      <div class="auth-card-container">
        <v-form ref="form" v-model="valid">
          <div class="auth-card-container form-fields" style="margin-bottom: 8px">
            <v-text-field
              autofocus
              name="firstname"
              validate-on="blur"
              v-model="firstname"
              :maxLength="40"
              hint="At most 40 characters"
              :rules="[rules.required]"
              variant="outlined"
              density="default"
              label="First Name"
            >
            </v-text-field>

            <v-text-field
              name="lastname"
              validate-on="blur"
              v-model="lastname"
              :maxLength="40"
              hint="At most 40 characters"
              :rules="[rules.required]"
              variant="outlined"
              density="default"
              label='Last Name'
            >
            </v-text-field>

            <v-text-field
              name="phoneNumber"
              label="Phone number"
              validate-on="blur"
              v-model="phoneNumber"
              :maxLength="25"
              :rules="[rules.phoneNumber]"
              variant="outlined"
              density="default"
              hint="Must start with a + sign followed by country code and numbers without spaces or punctuation"
            ></v-text-field>

            <v-text-field
              name="organisation"
              label="Organization"
              validate-on="blur"
              v-model="company"
              :maxLength="100"
              :rules="[rules.required]"
              hint="An organization is an entity that runs one or several development projects"
              variant="outlined"
              density="default"
            >
            </v-text-field>

            <v-text-field
              name="email"
              label="Email"
              type="email"
              autocomplete="none"
              validate-on="blur"
              :maxLength="254"
              v-model="email"
              :rules="[rules.required, rules.email]"
              variant="outlined"
              density="default"
            >
            </v-text-field>

            <v-text-field
              id="password"
              name="password"
              autocomplete="new-password"
              counter
              v-model="password"
              :maxLength="254"
              :append-inner-icon="e1 ? 'mdi-eye-off' : 'mdi-eye'"
              @click:append-inner="() => (e1 = !e1)"
              :type="e1 ? 'password' : 'text'"
              :rules="[rules.required, rules.passwordPolicy]"
              min="8"
              label="Password"
              :hint="PASSWORD_HINT"
              variant="outlined"
              density="default"
            >
            </v-text-field>

            <v-textarea
              name="referralSource"
              label="How did you hear about us?"
              validate-on="blur"
              v-model="referralSource"
              :maxLength="100"
              :rules="[rules.minCharacters]"
              variant="outlined"
              density="default"
              hint="At most 100 characters"
            >
            </v-textarea>

            <v-textarea
              name="usageIntent"
              label="How do you intend to use Continuum Design?"
              validate-on="blur"
              v-model="usageIntent"
              :maxLength="250"
              variant="outlined"
              density="default"
              hint="At most 250 characters"
            >
            </v-textarea>

            <div class="d-flex align-center" v-if="paywallEnabled">
              <img
                class="bullet-checked"
                alt="Continuum Design"
                src="@/assets/compare-checked.svg"
              />
              <v-label class="text-wrap">
                <span>A card is required, but you won't be charged for 4 weeks when signing up for the Pro Plan. Cancel anytime.</span>
              </v-label>
            </div>
            <v-checkbox
              v-model="checkbox"
              :rules="[rules.terms]"
              class="d-inline-block mt-0"
            >
              <template v-slot:input>
                <div @click="checkbox = !checkbox" class="d-flex align-center">
                  <v-icon v-if="!checkbox">
                    mdi-checkbox-blank-outline
                  </v-icon>
                  <img
                    v-else
                    class="bullet-checked"
                    alt="Continuum Design"
                    src="@/assets/compare-checked.svg"
                  />
                </div>
              </template>
              <template v-slot:label>
                <div>
                  <span @click="checkbox = !checkbox">I agree to the </span>
                  <strong
                  ><u
                  ><a
                    onclick="window.open('../../Continuum_Design_terms_of_service_and_privacy_policy.pdf','_blank');"
                    href="#"
                  >Terms of Service & Privacy Policy</a
                  ></u
                  ></strong
                  >
                </div>
              </template>
            </v-checkbox>

            <p class="message bg-error" v-if="error">{{ error }}</p>
            <p class="message bg-indigo" v-if="message">{{ message }}</p>
          </div>

          <div class="auth-card-container">
            <div class="auth-card-footer justify-space-between">
              <div class="auth-card-footer-part">
                <p class="text">
                  Already have an account?
                  <router-link
                    class="text text-decoration-none"
                    tabindex="5"
                    :to="{ path: '/login' }"
                  >
                    <span class="light--text">Log in</span>
                  </router-link>
                </p>
              </div>
              <div class="auth-card-footer-part">
                <v-btn rounded="lg" size="large" color="primary" class="ml-auto d-flex" variant="flat" @click="submit"
                       tabindex="3" :disabled="!checkbox">
                  Create account
                </v-btn>
              </div>
            </div>
          </div>
        </v-form>
      </div>
    </div>
  </div>
</template>

<script>

// eslint-disable-next-line no-unused-vars
import { passwordValidationRule, PASSWORD_HINT } from '@/helpers/passwordValidation'
import { authProxyApi } from '@/api/authProxyApi'
import { isPaywallEnabled } from '@/utils/featureFlags'

export default {
  data () {
    return {
      checkbox: false,
      dialog: false,
      e1: true,
      email: '',
      error: null,
      message: null,
      firstname: '',
      lastname: '',
      password: '',
      referralSource: '',
      usageIntent: '',
      company: null,
      phoneNumber: null,
      rules: {
        required: (value) => !!value || 'Required',
        email: (value) => {
          const pattern = /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/
          return pattern.test(value) || 'Invalid e-mail'
        },
        phoneNumber: (value) => {
          const pattern = /^\+[1-9]\d{1,14}$/
          return !value || pattern.test(value) || 'Invalid phone number. Must start with a + sign followed by country code and numbers without spaces or punctuation'
        },
        terms: (value) => value || 'You must agree to continue!',
        minCharacters: (value) => (value && value.length >= 10) || 'Please enter at least 10 characters',
        passwordPolicy: passwordValidationRule
      },
      valid: true
    }
  },
  computed: {
    paywallEnabled () {
      return isPaywallEnabled()
    }
  },
  methods: {
    async submit () {
      this.error = null
      this.message = null
      const { valid } = await this.$refs.form.validate()
      if (valid) {
        // Maintain a minimum delay to keep timing roughly consistent across
        // outcomes. Account existence is disclosed via response code for
        // EMAIL_TAKEN by design (matches Google/Microsoft signup UX), but we
        // still avoid leaking timing on unrelated transient errors.
        const start = Date.now()
        const minDelayMs = 800

        const email = (this.email || '').toLowerCase()
        this.email = email

        let response = null
        try {
          response = await authProxyApi.signup({
            email,
            password: this.password,
            firstname: this.firstname,
            lastname: this.lastname,
            company: this.company,
            phoneNumber: this.phoneNumber,
            referralSource: this.referralSource,
            usageIntent: this.usageIntent
          })
        } catch (e) {
          // Network/transport errors: keep generic to avoid leaking timing/state.
          console.error('Signup via auth proxy failed:', e)
        }

        const remaining = minDelayMs - (Date.now() - start)
        if (remaining > 0) {
          await new Promise(resolve => setTimeout(resolve, remaining))
        }

        // Coded failures from the backend (EMAIL_TAKEN, SIGNUP_FAILED,
        // INVALID_PASSWORD, INVALID_INPUT). Render the backend message in-place
        // and stay on the form. EMAIL_TAKEN's message already directs the user
        // to log in or reset password, so the existing footer links cover the
        // recovery actions — no need for a dedicated alert.
        if (response?.ok === false) {
          this.error = response.message || 'We couldn\'t complete your sign-up. Please try again.'
          return
        }

        // Success path (or no response from a network/transport error):
        // redirect to /confirm so a legitimate signup can complete with
        // the emailed code.
        this.$router.replace({
          path: '/confirm',
          query: { username: email, projectId: this.$route.query.projectId, redirect: this.$route.query.redirect }
        })
      } else {
        this.error = 'One or more fields are invalid.'
      }
    }
  },
  mounted () {
    window.trackSignupPageView()
  }
}
</script>

<style scoped>
.text {
  color: var(--text-dark);
  font-size: 13px;
  text-align: left;
  margin-bottom: 0;
  line-height: 14.3px;
}

.text-decoration-none {
  text-decoration: none;
}

.logo {
  width: 180px;
  margin-bottom: 24px;
}

.form-fields {
  gap: 12px;
  display: flex;
  flex-direction: column;
}

.message {
  padding: 16px;
  border-radius: 8px;
  margin-bottom: 16px;
}

.bullet-checked {
  width: 30px;
  height: 30px;
  margin: 0 5px 0 5px;
}

[data-theme="dark"] a,
[data-theme="dark"] a .light--text {
  color: var(--blue-1);
}

</style>
